import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', approved: 'bg-blue-100 text-blue-700',
  deployed: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700',
  rolled_back: 'bg-amber-100 text-amber-700',
};
const CAT_ICONS = { schema: '🗃️', data: '📊', procedure: '⚙️', index: '🔍', hotfix: '🔥' };

export default function Migrations() {
  const [migrations, setMigrations] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list'); // list | create | sync
  const [selected, setSelected] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [toast, setToast] = useState(null);

  // Create form state
  const [form, setForm] = useState({
    migration_id: '', name: '', description: '',
    sql_up: '', sql_down: '', category: 'schema', is_breaking: false,
  });

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const fetchMigrations = useCallback(async () => {
    try {
      const { data } = await api.get('/migrations');
      setMigrations(data.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/migrations/sync-status');
      setSyncStatus(data.data);
    } catch {}
  }, []);

  useEffect(() => { fetchMigrations(); fetchSyncStatus(); }, [fetchMigrations, fetchSyncStatus]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.migration_id || !form.name || !form.sql_up) { showToast('ID, Name, and SQL Up are required', 'error'); return; }
    try {
      await api.post('/migrations', form);
      showToast('Migration created as draft');
      setForm({ migration_id: '', name: '', description: '', sql_up: '', sql_down: '', category: 'schema', is_breaking: false });
      setActiveTab('list');
      fetchMigrations();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to create', 'error'); }
  };

  const handleApprove = async (id) => {
    if (!window.confirm(`Approve migration "${id}"?`)) return;
    try {
      await api.post(`/migrations/${id}/approve`);
      showToast('Migration approved');
      fetchMigrations();
      if (selected?.migration_id === id) fetchDetail(id);
    } catch (err) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleDeploy = async (id) => {
    if (!window.confirm(`Deploy migration "${id}" to ALL active tenants?`)) return;
    setDeploying(true);
    try {
      const { data } = await api.post(`/migrations/${id}/deploy`);
      const r = data.data;
      showToast(`Deployed: ${r.success} success, ${r.failed} failed, ${r.skipped || 0} skipped`);
      fetchMigrations(); fetchSyncStatus();
      if (selected?.migration_id === id) fetchDetail(id);
    } catch (err) { showToast(err.response?.data?.message || 'Deploy failed', 'error'); }
    finally { setDeploying(false); }
  };

  const handleRollback = async (id) => {
    if (!window.confirm(`Rollback migration "${id}" from all tenants? This will execute the rollback SQL.`)) return;
    try {
      const { data } = await api.post(`/migrations/${id}/rollback`);
      showToast(`Rolled back: ${data.data.success} success, ${data.data.failed} failed`);
      fetchMigrations();
    } catch (err) { showToast(err.response?.data?.message || 'Rollback failed', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete migration "${id}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/migrations/${id}`);
      showToast('Migration deleted');
      setSelected(null);
      fetchMigrations();
    } catch (err) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleSyncAll = async () => {
    if (!window.confirm('Apply all missing migrations to ALL active tenants?')) return;
    setDeploying(true);
    try {
      const { data } = await api.post('/migrations/sync-all');
      showToast(`Synced ${data.data.totalSynced} migration(s) across ${data.data.tenants} tenants`);
      fetchSyncStatus(); fetchMigrations();
    } catch (err) { showToast(err.response?.data?.message || 'Sync failed', 'error'); }
    finally { setDeploying(false); }
  };

  const handleSyncTenant = async (code) => {
    try {
      const { data } = await api.post(`/tenants/${code}/sync`);
      showToast(`Tenant ${code}: synced ${data.data.synced} migration(s)`);
      fetchSyncStatus();
    } catch (err) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const fetchDetail = async (id) => {
    try {
      const { data } = await api.get(`/migrations/${id}`);
      setSelected(data.data);
    } catch {}
  };

  const generateId = () => {
    const d = new Date();
    const prefix = `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`;
    const seq = String(migrations.filter(m => m.migration_id.startsWith(prefix)).length + 1).padStart(3, '0');
    setForm(f => ({ ...f, migration_id: `${prefix}_${seq}_` }));
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Database Migrations</h2>
          <p className="text-sm text-gray-500">Manage schema updates across all tenant databases</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSyncAll} disabled={deploying}
            className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl text-sm font-medium shadow-sm disabled:opacity-50">
            {deploying ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '🔄'}
            Sync All Tenants
          </button>
          <button onClick={() => { setActiveTab('create'); generateId(); }}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-medium shadow-sm">
            + New Migration
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[['list','Migrations'],['sync','Sync Status'],['create','Create New']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ═══ LIST TAB ═══ */}
      {activeTab === 'list' && (
        <div className="space-y-4">
          {migrations.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center text-gray-400">
              No migrations yet. Create one to push schema updates to all tenants.
            </div>
          ) : (
            <div className="space-y-3">
              {migrations.map(m => (
                <div key={m.migration_id} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span>{CAT_ICONS[m.category] || '📄'}</span>
                        <h4 className="text-sm font-semibold text-gray-900 truncate">{m.name}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[m.status]}`}>{m.status}</span>
                        {m.is_breaking ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">⚠ Breaking</span> : null}
                      </div>
                      <p className="text-xs text-gray-500 font-mono">{m.migration_id}</p>
                      {m.description && <p className="text-xs text-gray-400 mt-1 line-clamp-1">{m.description}</p>}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span>Created: {new Date(m.created_at).toLocaleDateString()}</span>
                        {m.created_by_name && <span>by {m.created_by_name}</span>}
                        <span className="text-green-600 font-medium">✓ {m.deployed_count || 0}/{m.total_tenants || 0} tenants</span>
                        {m.failed_count > 0 && <span className="text-red-600 font-medium">✗ {m.failed_count} failed</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      <button onClick={() => fetchDetail(m.migration_id)} className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                        View
                      </button>
                      {m.status === 'draft' && (
                        <button onClick={() => handleApprove(m.migration_id)} className="px-3 py-1.5 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors">
                          Approve
                        </button>
                      )}
                      {['approved', 'deployed'].includes(m.status) && (
                        <button onClick={() => handleDeploy(m.migration_id)} disabled={deploying}
                          className="px-3 py-1.5 text-xs bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors disabled:opacity-50">
                          {deploying ? '...' : 'Deploy'}
                        </button>
                      )}
                      {m.status === 'deployed' && (
                        <button onClick={() => handleRollback(m.migration_id)} className="px-3 py-1.5 text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg transition-colors">
                          Rollback
                        </button>
                      )}
                      {['draft', 'rolled_back'].includes(m.status) && (
                        <button onClick={() => handleDelete(m.migration_id)} className="px-3 py-1.5 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors">
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Detail Panel */}
          {selected && (
            <div className="bg-white rounded-xl border border-blue-200 p-5 shadow-sm mt-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{selected.name}</h3>
                  <p className="text-xs text-gray-500 font-mono">{selected.migration_id}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
              </div>
              {selected.description && <p className="text-sm text-gray-600 mb-3">{selected.description}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">SQL Up (Apply)</label>
                  <pre className="bg-gray-50 rounded-lg p-3 text-xs font-mono text-gray-700 max-h-48 overflow-auto border">{selected.sql_up}</pre>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">SQL Down (Rollback)</label>
                  <pre className="bg-gray-50 rounded-lg p-3 text-xs font-mono text-gray-500 max-h-48 overflow-auto border">{selected.sql_down || '— No rollback SQL defined —'}</pre>
                </div>
              </div>
              {/* Deployment logs */}
              {selected.deployment?.logs?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 mb-2 uppercase">Deployment Log ({selected.deployment.deployed} / {selected.deployment.total_tenants} tenants)</h4>
                  <div className="max-h-48 overflow-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-gray-50"><th className="px-3 py-1.5 text-left">Tenant</th><th className="px-3 py-1.5 text-left">Status</th><th className="px-3 py-1.5 text-left">Time</th><th className="px-3 py-1.5 text-left">Error</th></tr></thead>
                      <tbody>
                        {selected.deployment.logs.map((l, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-3 py-1.5 font-mono">{l.tenant_code} {l.tenant_name && <span className="text-gray-400">({l.tenant_name})</span>}</td>
                            <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${l.status === 'success' ? 'bg-green-100 text-green-700' : l.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{l.status}</span></td>
                            <td className="px-3 py-1.5 text-gray-500">{l.execution_time_ms}ms</td>
                            <td className="px-3 py-1.5 text-red-500 truncate max-w-[200px]">{l.error_message || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ SYNC STATUS TAB ═══ */}
      {activeTab === 'sync' && (
        <div className="space-y-4">
          {syncStatus && (
            <div className="bg-white rounded-xl border p-4 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Platform Sync Status — {syncStatus.totalMigrations} deployed migration(s)
                </h3>
                <button onClick={handleSyncAll} disabled={deploying}
                  className="text-xs bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-1.5 rounded-lg font-medium disabled:opacity-50">
                  {deploying ? 'Syncing...' : 'Sync All'}
                </button>
              </div>
              {syncStatus.totalMigrations === 0 ? (
                <p className="text-sm text-gray-400">No deployed migrations. Create and deploy one to track sync status.</p>
              ) : (
                <div className="space-y-2">
                  {syncStatus.tenants?.map(t => (
                    <div key={t.code} className={`flex items-center justify-between px-4 py-3 rounded-lg border ${t.synced ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-2.5 h-2.5 rounded-full ${t.synced ? 'bg-green-500' : 'bg-amber-500'}`} />
                        <div>
                          <span className="text-sm font-medium text-gray-900">{t.name}</span>
                          <span className="text-xs text-gray-400 ml-2 font-mono">{t.code}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">
                          {t.applied}/{syncStatus.totalMigrations} applied
                          {t.missing > 0 && <span className="text-amber-600 font-medium ml-1">({t.missing} missing)</span>}
                        </span>
                        {!t.synced && (
                          <button onClick={() => handleSyncTenant(t.code)}
                            className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1 rounded-lg font-medium">
                            Sync
                          </button>
                        )}
                        {t.synced && <span className="text-xs text-green-600 font-medium">✓ In Sync</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ CREATE TAB ═══ */}
      {activeTab === 'create' && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm space-y-4">
          <h3 className="text-base font-semibold text-gray-900">Create New Migration</h3>
          <p className="text-xs text-gray-400">Define a SQL change that will be applied to all tenant databases. Follow the draft → approve → deploy workflow.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Migration ID *</label>
              <div className="flex gap-2">
                <input value={form.migration_id} onChange={e => setForm(f => ({ ...f, migration_id: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') }))}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono" placeholder="20260216_001_add_gst_column" required />
                <button type="button" onClick={generateId} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs">Auto</button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Add GST column to voucher entries" required />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Adds gst_rate and gst_amount columns..." />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="schema">Schema (ALTER TABLE, CREATE TABLE)</option>
                <option value="data">Data (INSERT, UPDATE reference data)</option>
                <option value="procedure">Procedure (CREATE/ALTER PROCEDURE)</option>
                <option value="index">Index (ADD/DROP INDEX)</option>
                <option value="hotfix">Hotfix (urgent fix)</option>
              </select>
            </div>
            <div className="flex items-center gap-3 pt-5">
              <input type="checkbox" checked={form.is_breaking} onChange={e => setForm(f => ({ ...f, is_breaking: e.target.checked }))} className="w-4 h-4 text-red-600 rounded" />
              <label className="text-sm text-gray-700">Breaking change (may disrupt running application)</label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">SQL Up (Apply) *</label>
              <textarea value={form.sql_up} onChange={e => setForm(f => ({ ...f, sql_up: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono h-40" required
                placeholder="ALTER TABLE inword_vchentry ADD COLUMN gst_rate DECIMAL(5,2) DEFAULT 0;" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">SQL Down (Rollback) <span className="text-gray-400">— optional</span></label>
              <textarea value={form.sql_down} onChange={e => setForm(f => ({ ...f, sql_down: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono h-40"
                placeholder="ALTER TABLE inword_vchentry DROP COLUMN gst_rate;" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setActiveTab('list')} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm">
              Create as Draft
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
